// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { Flex, Stack, Text } from '@mantine/core';

import { isNil, isNotNil } from 'helpers/isNotNil';
import { BendPreview } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreview';
import { getBasicBendSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

export const BendCreatorBendPreview = () => {
  const { conduitSizes } = useConduits();
  const { lastValidation } = useBendCreatorValidationContext();
  const { values } = useBendCreatorFormContext();
  const currentBendTypeName = values.bendType;

  const bendPreviewData = useMemo(() => {
    if (isNil(lastValidation) || !lastValidation.isSuccess) return null;
    const { response, request } = lastValidation;
    // Don't show the preview if the bend type has changed since the last validation.
    if (request.bendType !== currentBendTypeName) return null;
    // We're given conduit size as an ID, but we need to know the measured size of the conduit.
    // If we can't find the conduit size, we can't generate the bend preview.
    const conduitSize = conduitSizes?.find(({ conduitSizeId }) => conduitSizeId === request.conduitSizeId)?.diameter;
    if (isNil(conduitSize)) return null;
    const { bendRadius, ybcData } = response;
    const baseBend = getBasicBendSpecifications({ bendRadius, conduitSize });
    response.updatedBend.forEach(({ keyName, value }) => {
      baseBend[keyName] = value;
    });
    if (isNotNil(ybcData)) {
      baseBend.ybcString = ybcData;
    }
    return baseBend;
  }, [lastValidation, currentBendTypeName, conduitSizes]);

  return (
    <Flex p="md" flex={1} justify="center" display="flex" h="100%">
      {isNotNil(currentBendTypeName) ? (
        <Stack flex={1} align="center" h="100%">
          <BendPreview bendType={currentBendTypeName} bendPreviewData={bendPreviewData} />
        </Stack>
      ) : (
        <Stack justify="center">
          <Text c="dimmed">Select a bend type and your preview will appear here.</Text>
        </Stack>
      )}
    </Flex>
  );
};
